/**
 * Workforce Explorer — the API contract this screen codes against, plus the
 * metric-definition plumbing that feeds every tooltip on the page.
 *
 * The `workforceService` methods live in packages/shared (organizationService),
 * which this page does NOT own. The seven read endpoints are a fixed contract,
 * so the response shapes are declared here once and the service is adapted to
 * them at a single, documented boundary (`workforceApi`) rather than sprinkling
 * casts through the panels. If the service and this file ever disagree, THIS is
 * the file to change — the panels stay untouched.
 *
 * Endpoints used (all GET, all under /api/v1/identity/org/workforce):
 *   /periods  /summary  /trend  /breakdown  /panels  /metric-definitions
 *   /export.xlsx  (permission-gated separately — see canExport on the page)
 *
 * No other request path is ever constructed here.
 */
import type { ReactNode } from 'react';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { workforceService } from '@/services/api/organizationService';

// =============================================================================
// CONTRACT TYPES
// =============================================================================

export type WorkforceBasis = 'employees' | 'engaged' | 'positions';
export type WorkforceGroupBy = 'function' | 'contract' | 'campus' | 'grade' | 'department';

export interface WorkforcePeriod {
  as_at: string;
  label: string;
  sub?: string | null;
  kind?: 'historical' | 'live' | string;
  is_live?: boolean;
  source_sync_at?: string | null;
}

export interface WorkforcePeriodsResponse {
  periods: WorkforcePeriod[];
  generated_at?: string | null;
  budget_aligned?: boolean;
}

export interface WorkforceTile {
  key: string;
  label: string;
  /** null is a real possibility (suppressed / not computed) — render a dash. */
  value: number | string | null;
  sub?: string | null;
  definition?: string | null;
}

export interface WorkforcePositionsBlock {
  approved: number;
  filled: number;
  vacant: number;
  fill_rate?: number | null;
}

/** One contract's column in the vacancy step-down. */
export interface VacancyContractRow {
  seats: number;
  filled: number;
  vacant: number;
  capacity_provisions: number;
  open: number;
  unfunded_vacant: number;
  vacancies: number;
  now: number;
  later: number;
  funded_seats: number;
  open_rate: number | null;
  unfunded_incumbents: number;
  no_line_incumbents: number;
}

export interface VacancyDepartmentRow {
  code: string;
  vacancies: number;
  now: number;
  later: number;
  permanent: number;
  ftc: number;
}

/**
 * THE VACANCY, on the definition the board reports — not "approved less filled".
 *
 *   vacant employee seats
 *     less lecturer capacity provisions
 *     = open positions
 *     less seats the 2026 budget does not fund
 *     = vacancies
 *
 * Present on the LIVE frame only: the budget is current state, so applying it to
 * a historical establishment would date the seats and the money differently.
 */
export interface WorkforceVacancy {
  as_at: string;
  budget_year: number;
  budget_as_of?: string | null;
  seats: number;
  filled: number;
  vacant: number;
  capacity_provisions: number;
  open: number;
  unfunded_vacant: number;
  vacancies: number;
  now: number;
  later: number;
  /** The month "activates later" starts from — it moves with the reporting date. */
  later_from_month: number;
  provisions_funded: number;
  funded_seats: number;
  open_rate: number | null;
  by_contract: Record<string, VacancyContractRow>;
  by_department: VacancyDepartmentRow[];
  aging: Record<string, number>;
  budget_matrix: Record<string, { filled: number; open: number; provisions: number; total: number }>;
  /** In post against a seat whose budget line carries nothing. */
  unfunded_incumbents: number;
  /** In post against a seat with no 2026 budget line at all. */
  no_line_incumbents: number;
  /** The pack's headline: both of the above. */
  incumbents_without_budget: number;
  benchmarks: { healthy_vacancy_rate: number; salary_savings_assumption: number };
}

/** What the board pack published at this date, frozen. */
export interface WorkforcePublished {
  source: string;
  positions?: { approved?: number; filled?: number };
  employee_positions?: { approved?: number; filled?: number };
  vacancy?: Record<string, unknown>;
}

export type WorkforcePublishedDrift = Record<
  string,
  { published: number; rebuilt: number; delta: number }
>;

export interface WorkforceSummary {
  as_at: string;
  basis: WorkforceBasis;
  label?: string | null;
  tiles: WorkforceTile[];
  positions: WorkforcePositionsBlock;
  people: Record<string, number>;
  employees: number;
  engaged: number;
  vacancy?: WorkforceVacancy | null;
  published?: WorkforcePublished | null;
  /** Present only where the rebuild no longer reproduces the published figures. */
  published_drift?: WorkforcePublishedDrift | null;
}

export interface WorkforceTrendPoint {
  as_at: string;
  label: string;
  /** null leaves a gap in the series rather than plotting a false zero. */
  value: number | null;
}

export interface WorkforceTrendSeries {
  key: string;
  label: string;
  color?: string | null;
  stacked?: boolean;
  points: WorkforceTrendPoint[];
}

export interface WorkforceTrend {
  basis: WorkforceBasis;
  title?: string | null;
  series: WorkforceTrendSeries[];
  note?: string | null;
}

export interface WorkforceBreakdownRow {
  key: string;
  label: string;
  /** null when the cell is suppressed under the min-n rule. NEVER render as 0. */
  value: number | null;
  share: number | null;
  suppressed?: boolean;
}

export interface WorkforceBreakdown {
  as_at: string;
  basis: WorkforceBasis;
  group_by: WorkforceGroupBy;
  total: number;
  rows: WorkforceBreakdownRow[];
  suppressed_rows: number;
  note?: string | null;
}

// -----------------------------------------------------------------------------
// PANELS — GET /panels exactly as identity serves it.
//
// NULL MEANS WITHHELD; ABSENT MEANS NOT AVAILABLE FOR THIS DATE.
// - A figure served as null was withheld to protect a small group: fewer than five
//   people, or a figure that would let such a group be worked out from the ones beside
//   it. Show it as withheld, never as 0, and never work it back from its neighbours.
//   The totals still include it.
// - A key the response leaves out is not available for this date: a copy stored
//   before the measure existed, a group with no managers, a month loaded before cost
//   was carried.
// - Where a null means something else, the field says so.
// -----------------------------------------------------------------------------

/**
 * One turnover cell: exits over the average number in post across the window.
 * `pct` null with `suppressed`: withheld with its count (the rate times the base gives
 * the count back). `pct` null without it: nobody was in post, so there is no rate.
 */
export interface TurnoverCell {
  /** null: withheld. */
  leavers: number | null;
  /** The average in post over the window, the rate's base. */
  avg: number;
  pct: number | null;
  suppressed: boolean;
  /** The two in-post counts behind `avg`. Absent on a copy stored before they were carried. */
  in_post_start?: number;
  in_post_end?: number;
  /** What `avg` counts: people (Permanent, FTC) or filled positions (ICA, Casual). */
  basis?: 'people' | 'filled_positions';
}

export interface WorkforceTurnover {
  by_category: Record<string, TurnoverCell>;
  /** Employees in post (Permanent + FTC): the headline on every basis. */
  employees_only: TurnoverCell | null;
  /** All categories together. Only a copy stored before the daily recalculation carries
   *  it: the recalculated block states each category on its own basis. */
  overall?: TurnoverCell;
}

/**
 * One cut of the manager set: a grade band, a manager role, or one side of the
 * managers-of-managers split. A withheld group is withheld whole: in a group under five,
 * the reports, average and range are one manager's own. A key left out: the group has no
 * managers (so no average or range), or the stored copy does not carry it.
 */
export interface WorkforceSpanGroup {
  managers?: number | null;
  reports?: number | null;
  avg_span?: number | null;
  min_span?: number | null;
  max_span?: number | null;
  suppressed: boolean;
}

export interface WorkforceSpanBand extends WorkforceSpanGroup {
  band: string;
}

export interface WorkforceSpanRole extends WorkforceSpanGroup {
  title: string;
}

export type ManagersOfManagersKey = 'manage_managers' | 'manage_individual_contributors';

/**
 * Where the basis's filled positions sit against the reporting lines. Only
 * `reporting_to_filled_seat` is inside the span figures. The buckets outside them are
 * withheld together whenever any of them holds a small group, zeros included. A key left
 * out is not carried by the stored copy.
 */
export interface WorkforceSpanCoverage {
  filled_seats?: number | null;
  with_line?: number | null;
  reporting_to_filled_seat?: number | null;
  reporting_to_vacant_seat?: number | null;
  /** Positions, not people. */
  vacant_seats_with_reports?: number | null;
  line_outside_establishment?: number | null;
  without_line?: number | null;
}

/**
 * The span figures counting stand-in leave approvers as the managers of people whose
 * manager's position is vacant. `reports` and `avg_span` are withheld with
 * `reports_through_stand_ins`, which is the difference of two published totals.
 */
export interface WorkforceSpanStandIns {
  managers?: number;
  reports?: number | null;
  avg_span?: number | null;
  median_span?: number;
  min_span?: number;
  max_span?: number;
  /** Empty where the basis publishes no distribution. */
  distribution: Record<string, number | null>;
  reports_through_stand_ins?: number | null;
}

export interface WorkforceSpan {
  managers: number;
  /** Absent on a copy stored before the reporting-line rework. */
  reports?: number;
  avg_span: number;
  median_span: number;
  min_span?: number;
  max_span: number;
  /** Managers by span bucket ("1-3", "4-6", "7-10", "11+"). null: withheld. The buckets add
   *  up to `managers`, so a withheld one must never be drawn from the others. Empty on the
   *  all-staff basis. */
  distribution: Record<string, number | null>;
  /** Employee basis only: empty on the other bases and on an older copy. */
  by_band: WorkforceSpanBand[];
  by_manager_role: WorkforceSpanRole[];
  managers_of_managers: Partial<Record<ManagersOfManagersKey, WorkforceSpanGroup>>;
  /** Absent on a copy stored before the reporting-line rework. */
  coverage?: WorkforceSpanCoverage;
  with_stand_ins?: WorkforceSpanStandIns;
  basis_note: string;
}

/**
 * One payroll month, organisation-wide. The paid series is never withheld: a null here is
 * a month with no figure, drawn as a gap.
 */
export interface WorkforcePayRow {
  month: string;
  headcount: number | null;
  fte: number | null;
  /** Recurring staff cost for everyone paid that month, in rand. Absent on months loaded before it was carried. */
  cost?: number;
  suppressed: boolean;
}

export interface WorkforceRatioRow {
  campus: string;
  /** null: the campus has no student figure. */
  students: number | null;
  /** null with `suppressed`: withheld (under five lecturer FTE); the ratio goes with it. */
  lecturer_fte: number | null;
  ratio: number | null;
  suppressed: boolean;
}

export interface WorkforceRatioTotal {
  students: number;
  lecturer_fte: number;
  ratio: number;
}

export interface WorkforceRatio {
  by_campus: WorkforceRatioRow[];
  /** null when the stored copy carries no complete total. */
  total: WorkforceRatioTotal | null;
}

export interface WorkforceWindow {
  from: string;
  to: string;
}

export interface WorkforcePanels {
  as_at: string;
  /** The frame that actually produced these panels. Windowed measures are
   *  computed on the latest frame only, so this differs from `as_at` whenever an
   *  earlier period is selected — the off-period banner keys off it. Never a
   *  panel's own date: see `panelDate`. */
  panels_as_at: string | null;
  /** The oldest date among the blocks not recomputed for the selected date; null
   *  when every block is current. The nightly job hands panels forward, so
   *  `panels_as_at` moves every night while the numbers inside may not. */
  panels_computed_for: string | null;
  panels_stale_days: number | null;
  /** Per-block vintage: {block: {computed_for, stale_days, kind, window}}. A panel
   *  must date itself from ITS OWN entry, never from `panels_as_at` — see
   *  `panelDate` and `panelDateLabel`. */
  panels_provenance: Record<string, PanelProvenanceEntry>;
  /** Blocks not recomputed for the selected date, oldest first. */
  panels_stale_blocks: string[];
  basis: WorkforceBasis;
  turnover: WorkforceTurnover | null;
  /** People who left on a contract extension or renewal code (CE, CR), set apart from
   *  the exits. null: withheld, or not carried for this date (`extras.conversions`
   *  says which: absent means not carried). */
  conversions: number | null;
  /** {reason: exits}: employees on the employee basis, every engaged worker otherwise.
   *  null: withheld. Empty when payroll's record of why people left could not be
   *  checked (`extras.exit_analysis.sage_payroll_checked` false). */
  exit_reasons: Record<string, number | null>;
  /** Server-authored provenance + suppression statement for this block. */
  note: string | null;
  /** How many figures across the panels are withheld. */
  suppressed_cells: number;
  /** The smallest group a figure is shown for. */
  min_n: number;
  span: WorkforceSpan | null;
  pay: WorkforcePayRow[];
  ratio: WorkforceRatio | null;
  /** The turnover window. Every other windowed block carries its own, in `panels_provenance`. */
  window: WorkforceWindow | null;
  /** Every other board-pack panel, by block. A block is absent when this date carries none. */
  extras: WorkforcePanelExtras;
}

/** `extras`, block by block. Read one with `readExtras(panels, key)`. */
export interface WorkforcePanelExtras {
  // Establishment
  campus_footprint?: CampusFootprintBlock;
  titles_trend?: TitlesTrendPoint[];
  titles_in_use?: TitlesInUse;
  role_catalogue?: RoleCatalogueBlock;
  role_contract_mix?: RoleMixRow[];
  role_contract_mix_removed?: RoleMixRemoved;
  ftc_tenure?: FtcTenure;
  // Structure (span of control is `span`, above)
  key_person?: KeyPersonExtra;
  // People
  equity?: EquityExtra;
  age_service?: AgeServiceExtra;
  sessional_profile?: SessionalProfileExtra;
  // Movement
  turnover?: TurnoverExtra;
  exit_analysis?: ExitAnalysisExtra;
  conversions?: ConversionsExtra;
  /** A list only on a copy stored before the daily recalculation. */
  exit_roles?: ExitRolesExtra | ExitRoleRowStored[];
  /** `MobilityStored` only on a copy stored before the daily recalculation. */
  mobility?: MobilityExtra | MobilityStored;
  // Platform
  /** `AdoptionStored` is a copy stored before the current definition: show none of it. */
  adoption?: AdoptionExtra | AdoptionStored;
  indicators?: IndicatorsExtra;
  // Finance payroll month
  ica_governance_fte?: IcaGovernanceFte;
  /** The seat-basis block `ica_governance_fte` supersedes, served as stored until it is retired. */
  ica_governance?: IcaGovernanceSeats;
  sessional_capacity?: SessionalCapacity;
  pay_cost?: PayCost;
  pay_reconciliation?: PayReconciliation;
  ratio_detail?: RatioDetail;
}

// ---- Establishment ----------------------------------------------------------

/** 'national': Central and Corporate Programmes, national functions rather than campuses.
 *  'other': the pooled "Other / not yet assigned" row. */
export type CampusType = 'campus' | 'national' | 'other';

/** The pooled row's code: positions with no campus recorded, and codes outside the campuses. */
export const CAMPUS_OTHER_CODE = 'OTHER';

/** One row of `extras.campus_footprint`: POSITIONS, not people. Every count is published. */
export interface CampusFootprintRow {
  code: string;
  name: string;
  /** Absent on a copy stored before the finalised pack. */
  campus_type?: CampusType;
  employee_total: number;
  employee_filled: number;
  employee_vacant: number;
  /** Filled ICA and casual positions. */
  sessional_filled: number;
  /** Approved ICA and casual positions, filled and vacant. Absent on an older copy. */
  sessional_total?: number;
  /** Absent on the pooled row, on a row with no employee position, and on an older copy.
   *  Never compute it on the page. */
  employee_vacancy_pct?: number;
  suppressed: boolean;
}

export interface CampusFootprintTotals {
  employee_total: number;
  employee_filled: number;
  employee_vacant: number;
  sessional_total: number;
  sessional_filled: number;
}

export interface CampusFootprintBlock {
  note: string | null;
  /** The campuses, then one pooled `CAMPUS_OTHER_CODE` row. */
  campuses: CampusFootprintRow[];
  totals?: CampusFootprintTotals;
  /** The codes pooled into the Other row. None has a row of its own. */
  other_codes?: string[];
}

/** One point of `extras.titles_trend`: distinct titles on approved positions at that date. */
export interface TitlesTrendPoint {
  as_at: string;
  titles: number;
}

export interface TitleFunctionCut {
  by_function: Record<string, number>;
  on_deprecated_role: number;
}

/** `extras.titles_in_use`, beside the `titles_trend` series. Absent on an older copy. */
export interface TitlesInUse extends Partial<TitleFunctionCut> {
  /** The role catalogue `by_function` reads. */
  catalogue_as_at?: string;
  /** Role status keeps no history; it is read on this date. */
  status_as_at?: string;
  current_catalogue?: TitleFunctionCut & { catalogue_as_at: string };
  growth?: { from: string; to: string; pct: number };
  /** Approved positions with no title yet, not counted in the series. */
  untitled_seats?: number;
}

export interface CatalogueReading {
  active: number;
  non_graded: number;
  by_function: Record<string, number>;
  by_band: Record<string, number>;
  non_graded_with_filled_seat?: number;
}

/** `extras.role_catalogue`: ROLES, not people. The top-level keys are the reading of
 *  `catalogue_as_at`; today's reading rides beside it. */
export interface RoleCatalogueBlock extends CatalogueReading {
  catalogue_as_at?: string;
  status_as_at?: string;
  catalogue_changes?: {
    from: string;
    to: string;
    gained_a_grade: number;
    recorded_as_not_graded: number;
    new_role_codes: number;
  };
  current_catalogue?: CatalogueReading & { catalogue_as_at: string };
  computed_for?: string;
}

/** The key the untitled bucket carries; the page shows it as the board's label. */
export const ROLE_MIX_NO_TITLE = '(no title)';
export const ROLE_MIX_NO_TITLE_LABEL = 'Positions pending title assignment';

/** One title of `extras.role_contract_mix`: approved positions, filled and vacant. */
export interface RoleMixRow {
  title: string;
  /** null: a title on fewer than five positions, withheld whole with its four cells. */
  total: number | null;
  Permanent: number | null;
  FTC: number | null;
  ICA: number | null;
  Casual: number | null;
  /** The keys withheld in this row. */
  suppressed: string[];
}

/** `extras.role_contract_mix_removed`: the seasonal and temporary titles taken out of the ranking. */
export interface RoleMixRemoved {
  roles: RoleMixRow[];
  /** null: withheld, because a title among them is withheld whole. */
  positions: number | null;
  suppressed: string[];
}

/** `extras.ftc_tenure`: fixed-term employees in post, and how many have served two and three years. */
export interface FtcTenure {
  ftc_employees: number;
  two_years_plus: number | null;
  three_years_plus: number | null;
  suppressed: string[];
}

// ---- Structure --------------------------------------------------------------

/** `extras.key_person`: senior roles (bands D to F) held by exactly one employee. Roles, not people. */
export interface KeyPersonExtra {
  count: number;
  /** A band the executive list names in full is published; null: withheld. */
  by_band: Record<string, number | null>;
  /** Public posts, named on the organisation chart. */
  exec_roles: { title: string; grade: string }[];
  d_by_function: { function: string; roles: number | null; suppressed: boolean }[];
  band_c_single: number;
  /** Employees aged 60 or over in bands D and above — a count of people. null with
   *  `sixty_plus_withheld`: fewer than five. null without it: not computed for this date,
   *  because a senior employee has no birth date on record (`sixty_plus_basis` says how many). */
  sixty_plus_seniors: number | null;
  sixty_plus_withheld: boolean;
  sixty_plus_basis?: { senior_employees: number; age_recorded: number };
}

// ---- People -----------------------------------------------------------------

export interface EquitySenior {
  people: number | null;
  by_band: Record<string, number | null>;
  female: number | null;
  female_pct: number | null;
  african: number | null;
  african_pct: number | null;
  /** African, Coloured and Indian together. */
  designated_ACI: number | null;
  designated_pct: number | null;
  race_unknown?: number | null;
  gender_unknown?: number | null;
}

/** Whole-population representation: the page's headline figures. */
export interface EquityShares {
  female?: number;
  female_pct?: number;
  african?: number;
  african_pct?: number;
  designated_ACI?: number;
  designated_pct?: number;
  race_unknown?: number | null;
  gender_unknown?: number | null;
}

/** `extras.equity`. Bands D, E and F are served as one column, "D-F". */
export interface EquityExtra {
  population: number;
  employees_in_post?: number;
  bands: string[];
  race_x_band: Record<string, Record<string, number | null>>;
  gender_x_band: Record<string, Record<string, number | null>>;
  band_totals: Record<string, number | null>;
  senior: EquitySenior;
  shares: EquityShares;
  in_post_not_employed?: number | null;
  role_not_in_catalogue?: number | null;
  disability_flagged?: number | null;
  foreign_nationals?: number | null;
  nationality_not_captured?: number | null;
}

/** `extras.age_service`: employees in post. */
export interface AgeServiceExtra {
  people: number;
  age_bands: Record<string, number | null>;
  service_bands: Record<string, number | null>;
  median_age?: number;
  median_service?: number;
  median_age_by_category: Record<string, number>;
  median_service_by_category: Record<string, number>;
  /** The same people as the 60+ age band, and withheld with it. */
  sixty_plus?: number | null;
  age_not_captured?: number | null;
  service_not_captured?: number | null;
}

/** `extras.sessional_profile`: engaged ICA and casual workers, not employees. */
export interface SessionalProfileExtra {
  count?: number;
  median_age?: number;
  median_service?: number;
  split: Record<string, number | null>;
  female?: number | null;
  /** Withheld with its count. */
  female_pct?: number | null;
  african?: number | null;
  african_pct?: number | null;
  gender_unknown?: number | null;
  race_unknown?: number | null;
  sixty_plus?: number | null;
  age_not_captured?: number | null;
  foreign_nationals?: number | null;
  nationality_not_captured?: number | null;
}

// ---- Movement ---------------------------------------------------------------

/** `extras.turnover`: what the turnover cells do not carry. */
export interface TurnoverExtra {
  window: WorkforceWindow;
  /** Employee positions at the window's start and end. */
  employee_seats_start: number;
  employee_seats_end: number;
  /** Exits from positions with no contract type recorded. */
  unclassified_leavers: number | null;
  /** Every engaged worker's exits. */
  all_engaged_leavers: number | null;
}

export type ReasonSource = 'dynamics_fo' | 'sage_payroll' | 'not_recorded';

export interface ExitAnalysisScope {
  leavers: number | null;
  /** Where each exit's reason came from. */
  sources: Partial<Record<ReasonSource, number | null>>;
  /** Absent when payroll's record of reasons could not be checked for this date. */
  by_reason?: Record<string, number | null>;
  by_category?: Record<string, Record<string, number | null>>;
}

/** `extras.exit_analysis`: the exit reasons behind `exit_reasons`, on both scopes. */
export interface ExitAnalysisExtra {
  window: WorkforceWindow | null;
  /** false: payroll's record could not be checked, and no split by reason is served. */
  sage_payroll_checked: boolean;
  reason_order?: string[];
  employees: ExitAnalysisScope;
  all_engaged: ExitAnalysisScope;
}

/** `extras.conversions`: people who LEFT on a contract extension (CE) or renewal (CR) code,
 *  set apart from the exits as the Remco data pack sets them apart. Not movement. */
export interface ConversionsExtra {
  window: WorkforceWindow | null;
  /** The same figure as `panels.conversions`. */
  leavers: number | null;
  by_code: Record<string, number | null>;
  /** Everyone who left in the window: the exits plus the leavers set apart. */
  all_leavers: number | null;
  /** Exits, already inside every exit count, on the other contract-extension codes. */
  extension_coded_exits?: number | null;
  descriptions?: Record<string, string>;
}

export interface ExitRoleRow {
  role: string;
  leavers: number | null;
  suppressed: boolean;
}

/** `extras.exit_roles`. A title with fewer than `ranking_floor` exits is never named: its
 *  exits are in `other_roles`. */
export interface ExitRolesExtra {
  window: WorkforceWindow | null;
  true_exits: number | null;
  roles: ExitRoleRow[];
  ranking_floor: number;
  /** `roles` counts titles folded in, not people. */
  other_roles: { roles: number; leavers: number | null };
  seasonal: { leavers: number | null; roles: ExitRoleRow[] };
}

/** A row of a copy stored before the daily recalculation. */
export interface ExitRoleRowStored {
  role: string;
  leavers: number | null;
  avg_in_post: number | null;
  /** Withheld with its count. */
  pct: number | null;
  suppressed: boolean;
}

/** Starts inside the pack's totals that are not movement inside the workforce. */
export interface StartKind {
  moves: number | null;
  people: number | null;
}

export interface MobilityScope {
  /** Starts in a different position, returns and restarts included. */
  moves: number | null;
  /** Everyone behind those starts. Never add people across kinds: one person can return, then move. */
  people: number | null;
  /** null with `suppressed`: withheld with its people. */
  rate: number | null;
  /** employees only: people in post. */
  in_post?: number;
  /** employees: filled employee positions; all_engaged: every filled position. */
  filled_positions?: number;
  suppressed: boolean;
  /** No position at all in the twelve months before the start. Absent on older blocks. */
  returners?: StartKind;
  /** Starting again the position held in the twelve months before. Absent on older blocks. */
  restarts?: StartKind;
}

export type MobilityDestination = 'Permanent' | 'FTC' | 'ICA' | 'Casual' | 'Unclassified' | 'not_recorded';

/** `extras.mobility`, employees first. */
export interface MobilityExtra {
  window: WorkforceWindow | null;
  /** The role catalogue the function and grade splits read. */
  role_catalogue_as_at?: string;
  employees: MobilityScope;
  all_engaged: MobilityScope;
  into_employee_positions?: number | null;
  destination?: Partial<Record<MobilityDestination, number | null>>;
  function_unplaced?: { positions: number; of: number };
  grade_unplaced?: { positions: number; of: number };
  function_published?: boolean;
  grade_published?: boolean;
  function?: Partial<Record<'within' | 'across' | 'not_recorded', number | null>>;
  grade?: Partial<Record<'higher' | 'same' | 'lower' | 'not_recorded', number | null>>;
}

/** A copy stored before the daily recalculation: only the four grade-move counts went through
 *  the small-group rule. Tell it apart with `'promotions' in mobility`. */
export interface MobilityStored {
  window?: WorkforceWindow;
  moves?: number;
  people?: number;
  rate?: number;
  promotions: number | null;
  sideways: number | null;
  downward: number | null;
  no_grade: number | null;
  within_function?: number;
  cross_function?: number;
  unresolved?: number;
  rate_numerator?: string;
  rate_denominator?: string;
  sessional_moves_excluded?: number;
  computed_for?: string;
}

// ---- Platform ---------------------------------------------------------------

export type RatingKey = 'EXCEPTIONAL' | 'FULL' | 'NOT_YET_FULL' | 'WEAK';
export type IndicatorKey = RatingKey | 'TSTR';

export interface ReviewCycleRef {
  review_type: 'MIDYEAR' | 'YEAREND';
  review_year: number;
  name?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
}

export type AdoptionMeasureKey =
  | 'onboarded'
  | 'checkin_completed'
  | 'calibrated'
  | 'profile_complete'
  | 'growth_plan';

/** `people` null: withheld, and `pct` with it. `pct` absent: no base. */
export interface AdoptionCell {
  people?: number | null;
  pct?: number | null;
  suppressed: boolean;
}

export interface AdoptionMeasure {
  people?: number | null;
  pct?: number | null;
  suppressed?: boolean;
  /** Permanent and fixed-term add up to the measure: withholding one withholds the other. */
  by_category?: Partial<Record<'Permanent' | 'FTC', AdoptionCell>>;
}

/** `extras.adoption`: five measures over eligible employees. */
export interface AdoptionExtra {
  cycle?: ReviewCycleRef;
  eligibility?: { categories: string[]; appointed_on_or_before: string };
  population?: {
    employees_in_post: number;
    appointed_after_cutoff: number;
    eligible: number;
    eligible_by_category: Partial<Record<'Permanent' | 'FTC', number | null>>;
    with_platform_account: number;
  };
  measures: Partial<Record<AdoptionMeasureKey, AdoptionMeasure>>;
  measure_order?: AdoptionMeasureKey[];
  profile_sections?: { qualifications: number; career_history: number; either: number; both: number };
  /** `unmatched` absent when no F&O worker was loaded. */
  growth_plan_records?: { holders: number; unmatched?: number };
  computed_for?: string;
}

/** A copy stored before the current definition (roster, waves, objectives ...). Show none of it. */
export interface AdoptionStored {
  measures?: undefined;
  [section: string]: unknown;
}

export interface IndicatorCell {
  /** The campus code, on campus rows. */
  code?: string;
  /** Everyone calibrated, too soon to rate included. null: withheld. */
  n?: number | null;
  rated?: number | null;
  full_plus?: number | null;
  below_full?: number | null;
  exceptional?: number | null;
  /** Absent: nobody rated. null: withheld, also while `rated` is shown. */
  full_plus_pct?: number | null;
  dist?: Partial<Record<IndicatorKey, number | null>>;
  /** true only when the rated count itself is withheld. */
  suppressed?: boolean;
}

export interface IndicatorHistoryPoint {
  /** "FY 2025", "Mid-2026". */
  label: string;
  /** The cycle's name, on F&O points only. */
  name?: string;
  source?: 'fo' | 'platform';
  population?: 'rated_fo_discussions' | 'all_calibrated';
  rated: number | null;
  full_plus?: number | null;
  fp_pct?: number | null;
  dist?: Partial<Record<RatingKey, number | null>>;
}

/** `extras.indicators`. Every section is optional: a copy stored before the current definition carries few. */
export interface IndicatorsExtra {
  cycle?: ReviewCycleRef;
  population?: {
    calibrated?: number | null;
    tstr?: number | null;
    rated?: number | null;
    employees_in_post: number | null;
    independent_contractors: number | null;
    /** Casual, unclassified and no longer in post, served as one cell. */
    other: number | null;
  };
  overall?: IndicatorCell;
  by_function?: Record<string, IndicatorCell>;
  by_campus?: Record<string, IndicatorCell>;
  by_kpa?: Record<string, IndicatorCell>;
  /** Ratings recorded, not people. */
  kpa_rows?: { rows: number; rated: number; tstr: number; unmapped: number; people?: number };
  eligible_employees?: IndicatorCell & { appointed_on_or_before?: string; people?: number };
  eligible_employees_by_function?: Record<string, IndicatorCell>;
  /** Oldest first; the last point is the reported cycle. */
  history?: IndicatorHistoryPoint[];
}

// ---- Finance payroll month --------------------------------------------------

/** A figure a board pack published that this block reads differently: the pack's figure, the
 *  same date read again today, and this block's figure, each difference with its cause. Bridge
 *  the published figure, never restate it. A null in `now` or `re_read` is withheld. */
export interface PackBridge {
  pack: string;
  tab: string;
  payroll_month: string;
  /** The date the pack read F&O on. */
  read_on: string;
  published: Record<string, number>;
  re_read: Record<string, number | null>;
  now: Record<string, number | null>;
  re_read_differs: string[];
  now_differs: string[];
  re_read_cause: string;
  now_cause: string;
}

export interface IcaGovernanceMetric {
  key: string;
  metric: string;
  /** Rendered verbatim, e.g. "<= 45%". */
  target: string;
  threshold: number;
  unit: '%' | 'x' | '';
  good: 'low' | 'high';
  value: number;
  /** The server's verdict: prefer it to parsing the target. */
  on_target: boolean;
  /** The two concentration metrics only: rows over the threshold, of the rows shown. */
  count?: number;
  of?: number;
}

export interface IcaGovernanceCutRow {
  campus?: string;
  faculty?: string;
  lecturer_fte: number | null;
  ica_fte: number | null;
  ica_pct: number | null;
  /** Absent on a withheld row. */
  breach?: boolean;
  people: number | null;
  suppressed: boolean;
}

/** `extras.ica_governance_fte`: the ICA deep dive on full-time equivalents, one payroll month. */
export interface IcaGovernanceFte {
  computed_for: string;
  basis: 'fte_capped';
  payroll_month: string;
  /** The date lecturer positions were read on: the payroll month's last day. */
  establishment_as_at: string;
  lecturer_fte: number;
  ica_fte: number;
  perm_fte: number;
  ftc_fte: number;
  other_fte?: number;
  lecturer_people: number;
  ica_people: number;
  perm_people: number;
  ftc_people: number;
  share_pct: { ICA: number; Permanent: number; FTC: number };
  students?: number;
  metrics: IcaGovernanceMetric[];
  threshold_pct: number;
  min_cut_fte: number;
  by_campus_fte: IcaGovernanceCutRow[];
  by_faculty_fte: IcaGovernanceCutRow[];
  campus_unplaced_fte: number;
  faculty_unplaced_fte: number;
  target_sources: Record<string, string>;
  published?: PackBridge[];
}

/** The seat-basis ICA block, served exactly as stored until it is retired. Its panel keeps its own reader. */
export type IcaGovernanceSeats = Record<string, unknown>;

/** Engaged at month end -> paid in the month. */
export interface SessionalCapacityBridge {
  /** These three are withheld together, zeros included: any one shown gives the other two back. */
  engaged_contractors_paid: number | null;
  engaged_contractors_not_paid: number | null;
  paid_contractors_not_engaged_as_contractor: number | null;
  /** null whenever `paid_contractors_not_engaged_as_contractor` is withheld: the categories add up to it. */
  paid_contractors_by_category: Record<string, number | null> | null;
  engaged_casual_on_payroll: number | null;
  engaged_casual_on_payroll_by_type: Record<string, number | null>;
  /** Withheld with `engaged_contractors_paid`. */
  engaged_contractors_paid_fte: number | null;
}

/** `extras.sessional_capacity`: engaged at month end -> paid in the month -> FTE -> students. */
export interface SessionalCapacity {
  computed_for: string;
  payroll_month: string;
  engaged_as_at: string;
  engaged: { ICA: number; Casual: number; total: number };
  /** contractor_lines and fte: every contractor payroll line; contractors: distinct people paid. */
  paid: { contractors: number; contractor_lines: number; fte: number };
  bridge: SessionalCapacityBridge;
  lecturer_fte?: number;
  ica_lecturer_fte?: number;
  students?: number;
  students_per_lecturer_fte?: number;
  students_per_ica_lecturer_fte?: number;
  published?: PackBridge[];
}

export interface PayCostWindow {
  /** "YYYY-MM" */
  from: string;
  to: string;
  months: number;
  net_revenue: number;
  staff_cost: number;
  staff_cost_pct_of_net_revenue: number;
  avg_monthly_fte: number;
  net_revenue_per_fte: number;
  staff_cost_per_fte: number;
}

export interface PayCostCampus {
  campus: string;
  codes: string[];
  net_revenue?: number;
  prior_net_revenue?: number;
  /** Money over fewer than five people is withheld, with the people count. Net revenue stays. */
  staff_cost: number | null;
  prior_staff_cost: number | null;
  cost_pct?: number | null;
  prior_cost_pct?: number | null;
  /** Distinct people on the campus's payroll lines in each window. */
  people: number | null;
  prior_people: number | null;
  suppressed: boolean;
}

/** `extras.pay_cost`: staff cost and productivity, year to the payroll month against a year earlier. */
export interface PayCost {
  computed_for: string;
  payroll_month: string;
  window: WorkforceWindow;
  prior_window: WorkforceWindow;
  current: PayCostWindow;
  prior: PayCostWindow;
  by_campus: PayCostCampus[];
  cost_without_campus_revenue: PayCostCampus[];
  monthly: { month: string; prior_month: string; cost_pct?: number; prior_cost_pct?: number }[];
}

export interface PayReconciliationSteps {
  employees_in_post: number;
  seat_no_payroll_line: number | null;
  seat_paid_as_contractor: number | null;
  seat_paid_learnership: number | null;
  seat_paid_internship: number | null;
  payroll_line_no_seat: number | null;
  payroll_line_contractor_seat: number | null;
  payroll_line_casual_seat: number | null;
  payroll_line_unclassified_seat: number | null;
}

/** `extras.pay_reconciliation`: Finance's paid employee headcount against employees in post. */
export interface PayReconciliation {
  computed_for: string;
  payroll_month: string;
  establishment_as_at: string;
  payroll_period: string;
  dates_aligned: boolean;
  employees_in_post: { total: number; Permanent: number; FTC: number };
  finance_paid_employees: { total: number; PERM: number; FIXED: number };
  difference: number;
  /** The bridge's steps added up by size. Withheld whenever any step is. */
  people_moving: number | null;
  bridge: PayReconciliationSteps & {
    employee_payroll_lines: number;
    employee_payroll_lines_recount: number;
    unpaid_lines: number;
    paid_employee_lines: number;
    not_counted_by_finance: number;
    finance_paid_employees: number;
    foots: boolean;
  };
  legs: Record<
    'Permanent' | 'FTC',
    PayReconciliationSteps & {
      paid_from_other_employee_seat: number | null;
      payroll_lines: number;
      unpaid_lines: number;
      paid_lines: number;
      finance_paid: number;
      residual: number;
    }
  >;
  control: {
    payroll_lines: number;
    learnership_lines: number;
    internship_lines: number;
    unpaid_lines: number;
    not_counted_by_finance: number;
    result: number;
    finance_all_types?: number;
    ties?: boolean;
  };
  memo: {
    employee_seats_paid_as_contractors: number | null;
    employee_seats_no_payroll_line: number | null;
    casual_workers: number | null;
  };
  plan: Partial<
    Record<'PERM' | 'FIXED' | 'ICA' | 'all', { plan_month?: number; plan_december?: number; actual?: number }>
  >;
  /** The keys withheld in the bridge and both legs, `paid_from_other_employee_seat` included. */
  withheld: string[];
  finance_by_campus?: { cells: number; tying: number; plan_lines_missing: { type: string; campus: string }[] };
  published?: PackBridge[];
}

export interface RatioGrowthYear {
  label: string;
  /** 31 July of that year: students and employees on the same date. */
  as_at: string;
  students: number;
  employees: number;
  students_per_employee: number;
  role_titles: number;
  /** Employees whose position carries no campus. */
  employees_without_campus: number;
  online_students: number;
  /** The view excluding online is left out when campus dimensions are missing. */
  online_employees?: number;
  students_ex_online?: number;
  employees_ex_online?: number;
  students_per_employee_ex_online?: number;
}

/** `extras.ratio_detail`: what the served `ratio` rows do not carry. */
export interface RatioDetail {
  computed_for: string;
  payroll_month: string;
  establishment_as_at: string;
  students_basis: string;
  reconciliation: {
    department: string;
    lecturer_fte: number;
    whole_department_fte: number;
    academic_support_fte: number;
    lecturer_fte_outside_department: number;
    ratio_lecturers_only: number;
    ratio_whole_department?: number;
  };
  campuses_without_lecturer_fte: { campus: string; students: number }[];
  lecturer_fte_without_students?: { campus: string; lecturer_fte: number }[];
  growth?: {
    as_at: string;
    students_basis: string;
    years: RatioGrowthYear[];
    index: { label: string; students: number; employees: number; role_titles: number }[];
    growth_pct: { students: number; employees: number; role_titles: number };
  };
  published?: PackBridge[];
}

export interface MetricDefinition {
  key: string;
  label?: string | null;
  definition: string;
  basis?: string | null;
  source?: string | null;
  caveats?: string[] | null;
}

export interface MetricDefinitionsResponse {
  metrics: MetricDefinition[];
}

// =============================================================================
// SERVICE FACADE — the one place the shared service is adapted to the contract
// =============================================================================

interface WorkforceApi {
  getPeriods(): Promise<WorkforcePeriodsResponse>;
  getSummary(params: { basis: WorkforceBasis; asAt: string }): Promise<WorkforceSummary>;
  getTrend(params: { basis: WorkforceBasis }): Promise<WorkforceTrend>;
  getBreakdown(params: {
    basis: WorkforceBasis;
    asAt: string;
    groupBy: WorkforceGroupBy;
  }): Promise<WorkforceBreakdown>;
  getPanels(params: { basis: WorkforceBasis; asAt: string }): Promise<WorkforcePanels>;
  getMetricDefinitions(): Promise<MetricDefinitionsResponse>;
  downloadWorkbook(params: { basis: WorkforceBasis; asAt: string }): Promise<Blob>;
  /** Starts a frame rebuild and returns immediately — see WorkforceSyncStatus. */
  startSync(): Promise<{ started: boolean; poll?: string; note?: string }>;
  getSyncStatus(): Promise<WorkforceSyncStatus>;
}

/**
 * The rebuild's own state. Two runs are reported, and they answer different
 * questions: `manual` is the button on this page, `scheduled` is the 07:00 SAST
 * job — which is the one that tells a reader whether triggering a sync is worth
 * doing at all.
 */
export interface WorkforceSyncStatus {
  manual: {
    running: boolean;
    started_at: string | null;
    finished_at: string | null;
    actor: string | null;
    published: boolean | null;
    duration_seconds: number | null;
    ok: boolean | null;
    error: string | null;
    summary: Record<string, unknown> | null;
  };
  scheduled: {
    enabled?: boolean | null;
    schedule?: string | null;
    next_run?: string | null;
    last_result?: Record<string, unknown> | null;
    error?: string | null;
  };
}

/**
 * Checked, not cast. The service module is owned elsewhere; declaring the shape
 * here and letting TypeScript prove `workforceService` satisfies it means a
 * drift on either side — a renamed method, a field that becomes nullable —
 * fails the build instead of surfacing as a wrong number on a board screen.
 */
export const workforceApi: WorkforceApi = workforceService;

/**
 * One `extras` block, typed by its key: `readExtras(panels, 'pay_cost')`. null when this
 * date carries no such block. The shared service types `extras` loosely (it is the
 * transport); `WorkforcePanelExtras` is the served shape.
 */
export function readExtras<K extends keyof WorkforcePanelExtras>(
  panels: WorkforcePanels | null | undefined,
  key: K,
): NonNullable<WorkforcePanelExtras[K]> | null;
/** The form the payroll report writes: `readExtras<PayCost>(panels, 'pay_cost')`. */
export function readExtras<T>(panels: WorkforcePanels | null | undefined, key: string): T | null;
export function readExtras(panels: WorkforcePanels | null | undefined, key: string): unknown {
  const extras = panels?.extras as unknown as Record<string, unknown> | undefined;
  const block = extras ? extras[key] : undefined;
  return block && typeof block === 'object' ? block : null;
}

// =============================================================================
// PALETTE — hard-coded hex, matching the prototype and UsageAdoptionDashboard
// =============================================================================

export const CATEGORY_COLORS: Record<string, string> = {
  Permanent: '#15305D',
  FTC: '#2E6FD9',
  ICA: '#0B7F72',
  Casual: '#CFAD80',
  Unclassified: '#B9C4D6',
};

export const CATEGORY_ORDER = ['Permanent', 'FTC', 'ICA', 'Casual', 'Unclassified'];

/** Categories excluded from the `employees` basis — faded, never hidden. */
export const SESSIONAL_CATEGORIES = ['ICA', 'Casual'];

export const SERIES_FALLBACK_COLORS = ['#15305D', '#2E6FD9', '#0B7F72', '#CFAD80', '#B9C4D6'];

export const INK = '#15305D';
export const ACCENT = '#2E6FD9';
export const GRID = '#eef2f7';
export const AXIS = '#e5e7eb';

export const CATEGORY_LABELS: Record<string, string> = {
  FTC: 'Fixed-term (FTC)',
};

export const categoryLabel = (key: string) => CATEGORY_LABELS[key] ?? key;

export const BASIS_OPTIONS: { id: WorkforceBasis; label: string; short: string }[] = [
  { id: 'employees', label: 'Employees (Perm + FTC)', short: 'Employees' },
  { id: 'engaged', label: 'Engaged workforce', short: 'Engaged workforce' },
  { id: 'positions', label: 'Approved positions', short: 'Approved positions' },
];

export const GROUP_BY_OPTIONS: { id: WorkforceGroupBy; label: string }[] = [
  { id: 'function', label: 'Function' },
  { id: 'contract', label: 'Contract' },
  { id: 'campus', label: 'Campus' },
  { id: 'grade', label: 'Grade' },
  // Employees by the F&O financial dimension the budget is drawn against, shown
  // by code. Function answers "where do our people work"; department answers
  // "where is the money held". The two do not tie row-for-row and neither is wrong.
  { id: 'department', label: 'Department' },
];

export const basisLabel = (basis: WorkforceBasis) =>
  BASIS_OPTIONS.find((b) => b.id === basis)?.short ?? basis;

// =============================================================================
// FORMATTERS
// =============================================================================

const NUM = new Intl.NumberFormat('en-GB');
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * THE DATE A PANEL MAY PUT ITS OWN NAME TO.
 *
 * Not `panels_as_at`. That is the frame the panels are stored on, and the nightly
 * job hands the existing panels forward to a new live frame every night without
 * recomputing them — so it advances daily while the numbers inside do not. Six
 * panels printed "Point-in-time at {panels_as_at}" over figures computed six
 * weeks earlier. A precise date that is wrong is worse than no date at all,
 * because it forecloses the reader's own suspicion.
 *
 * Returns the block's own `computed_for`, falling back to the frame date only
 * when the server has no per-block stamp (a frame written before provenance
 * existed). `stale` says whether the two disagree, so a panel can mark itself.
 */
export const panelDate = (
  panels: WorkforcePanels | null | undefined,
  block: string,
): {
  date: string | null;
  staleDays: number | null;
  stale: boolean;
  kind: PanelKind | null;
  window: WorkforceWindow | null;
} => {
  const entry = panels?.panels_provenance?.[block];
  const date = entry?.computed_for ?? panels?.panels_as_at ?? panels?.as_at ?? null;
  const staleDays = entry?.stale_days ?? null;
  // The server decides staleness per kind — a payroll month is not stale for
  // being dated at its month's end — so its list wins when it sent one.
  const stale = panels?.panels_stale_blocks
    ? panels.panels_stale_blocks.includes(block)
    : !!staleDays && staleDays > 0;
  return { date, staleDays, stale, kind: entry?.kind ?? null, window: entry?.window ?? null };
};

/** What a block's date MEANS, which depends on the kind of measure it is. */
export type PanelKind = 'point_in_time' | 'window' | 'payroll_month' | 'cycle';

export interface PanelProvenanceEntry {
  /** null when the block states no date and carries no window to date it by. */
  computed_for: string | null;
  stale_days: number | null;
  /** Absent on a block stored before kinds were stamped: read it as point-in-time. */
  kind?: PanelKind;
  /** The window a windowed block covers. */
  window?: WorkforceWindow;
}

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * The words a panel puts beside its figures to say when they are from.
 *
 * A state reads "Point-in-time at 7 Sep 2026"; a flow names its window; a review
 * measure is "as it stands" on a date; a Finance measure names its payroll month —
 * "July 2026 payroll" is the newest there is until Finance closes August, and
 * printing it as a point-in-time date would make a correct figure look stale.
 */
export const panelDateLabel = (panels: WorkforcePanels | null | undefined, block: string): string => {
  const { date, kind, window } = panelDate(panels, block);
  if (!date) return 'Date not available';
  switch (kind) {
    case 'payroll_month': {
      const m = /^(\d{4})-(\d{2})/.exec(date);
      return m ? `${MONTH_FULL[Number(m[2]) - 1]} ${m[1]} payroll` : `Payroll to ${fmtDay(date)}`;
    }
    case 'window':
      return window ? `${fmtDay(window.from)} – ${fmtDay(window.to)}` : `To ${fmtDay(date)}`;
    case 'cycle':
      return `Review cycle as it stands at ${fmtDay(date)}`;
    default:
      return `Point-in-time at ${fmtDay(date)}`;
  }
};

export const fmtNum = (n: number | null | undefined) =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : NUM.format(n);

export const fmtOne = (n: number | null | undefined) =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : n.toFixed(1);

export const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : `${n.toFixed(1)}%`;

/** "2026-07-31" -> "31 Jul 2026". Parsed by parts so no timezone can shift it. */
export function fmtDay(iso?: string | null): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTH_ABBR[Number(m[2]) - 1]} ${m[1]}`;
}

/** "2026-07" -> "Jul 2026". */
export function fmtMonth(ym?: string | null): string {
  if (!ym) return '—';
  const m = /^(\d{4})-(\d{2})/.exec(ym);
  if (!m) return ym;
  return `${MONTH_ABBR[Number(m[2]) - 1]} ${m[1]}`;
}

/**
 * Sync instants are stored UTC and read by South African users, so they render
 * SAST — the house convention (filenames UTC, anything a human reads SAST).
 */
export function fmtSyncStamp(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 16).replace('T', ' ');
  }
}

/** Short form for the chip badge: "02:41". */
export function fmtSyncTimeOnly(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return d.toISOString().slice(11, 16);
  }
}

// =============================================================================
// METRIC DEFINITIONS — /metric-definitions is the source of truth; these are
// the local fallbacks so a tooltip is NEVER empty if that one call fails.
// =============================================================================

type LocalDef = Omit<MetricDefinition, 'key'>;

const LOCAL_DEFINITIONS: Record<string, LocalDef> = {
  // ---- bases -------------------------------------------------------------
  basis: {
    label: 'Basis',
    definition:
      'Every figure on this page is stated on one of three bases. Employees counts people whose most permanent seat is Permanent or fixed-term. Engaged workforce counts everyone in post, sessional staff included. Approved positions counts funded seats, filled or not.',
    caveats: ['A person can hold more than one seat, so filled seats always exceed engaged people.'],
  },
  basis_employees: {
    label: 'Employees basis',
    definition:
      'People whose person category is Permanent or FTC. A person is classified by the most permanent seat they hold — Permanent beats FTC beats ICA beats Casual.',
  },
  basis_engaged: {
    label: 'Engaged workforce basis',
    definition:
      'Everyone in post across all filled seats: Permanent, FTC, ICA, Casual and unclassified.',
  },
  basis_positions: {
    label: 'Approved positions basis',
    definition:
      'Approved (budget-aligned) seats at the chosen date, whether filled or vacant. This is an establishment count, not a people count.',
  },
  // ---- headline metrics ---------------------------------------------------
  employees_in_post: {
    label: 'Employees in post',
    definition: 'People whose person category is Permanent or fixed-term, in post at the chosen date.',
    source: 'F&O point-in-time establishment (budget-aligned)',
  },
  engaged_people: {
    label: 'Engaged people',
    definition: 'Everyone in post at the chosen date across all filled seats, sessional included.',
    source: 'F&O point-in-time establishment (budget-aligned)',
  },
  approved_positions: {
    label: 'Approved positions',
    definition: 'Approved seats that existed at the chosen date, on the budget-aligned rule.',
    source: 'F&O point-in-time establishment (budget-aligned)',
  },
  filled: {
    label: 'Filled seats',
    definition: 'Approved seats with an occupant at the chosen date.',
    caveats: ['People can hold more than one seat, so filled seats exceed engaged people.'],
  },
  filled_seats: {
    label: 'Filled seats',
    definition: 'Approved seats with an occupant at the chosen date.',
    caveats: ['People can hold more than one seat, so filled seats exceed engaged people.'],
  },
  approved: {
    label: 'Approved positions',
    definition: 'Approved seats that existed at the chosen date, on the budget-aligned rule.',
  },
  vacant: {
    label: 'Vacant positions',
    definition: 'Approved seats standing empty at the chosen date.',
  },
  fill_rate: {
    label: 'Fill rate',
    definition: 'Filled seats as a percentage of approved positions at the chosen date.',
  },
  // ---- categories ---------------------------------------------------------
  Permanent: {
    label: 'Permanent',
    definition: 'People whose most permanent seat is a permanent contract.',
  },
  FTC: {
    label: 'Fixed-term (FTC)',
    definition: 'People whose most permanent seat is a fixed-term contract.',
  },
  ICA: {
    label: 'ICA',
    definition:
      'Independent contractor agreements — sessional academic staff. Excluded from the employee basis, reported separately.',
  },
  Casual: {
    label: 'Casual',
    definition:
      'Casual engagements, typically invigilation and marking pools. Excluded from the employee basis.',
  },
  Unclassified: {
    label: 'Unclassified',
    definition:
      'People in post whose seat carries no recoverable contract type. Usually a data-quality residue, not a real category.',
  },
  sessional: {
    label: 'Sessional',
    definition: 'ICA plus casual engagements — everyone outside the employee basis.',
  },
  share_of_engaged: {
    label: 'Share of engaged people',
    definition: 'Employees as a percentage of everyone in post at the chosen date.',
  },
  // ---- panels -------------------------------------------------------------
  trend: {
    label: 'Trend',
    definition:
      'Each point is an independent point-in-time reconstruction at that date, on the budget-aligned rule — not a running total carried forward.',
    source: 'F&O date-effective entities',
  },
  composition: {
    label: 'Workforce composition',
    definition:
      'People in post at the chosen date, each classified by the most permanent seat they hold.',
  },
  breakdown: {
    label: 'Breakdown',
    definition:
      'The chosen basis split across one dimension at the chosen date. Small cells are suppressed.',
  },
  breakdown_value: {
    label: 'Count',
    definition: 'The count on the selected basis for this group at the chosen date.',
    caveats: ['Cells below the minimum group size show a dash rather than a number.'],
  },
  breakdown_share: {
    label: 'Share',
    definition: 'This group as a percentage of the breakdown total.',
  },
  suppression: {
    label: 'Small-cell suppression',
    definition:
      'Groups with fewer than 5 people are shown as a dash. An aggregate that small identifies individuals, so the number is withheld rather than the row dropped — the group is still visible, only the count is not.',
  },
  group_function: {
    label: 'Function',
    definition: 'Role Master function of the seat (Academics, Sales and Marketing, and so on).',
  },
  group_contract: { label: 'Contract', definition: 'Contract type of the seat.' },
  group_campus: { label: 'Campus', definition: 'Campus the seat sits at.' },
  group_grade: { label: 'Grade', definition: 'Grade band of the seat.' },
  // ---- turnover -----------------------------------------------------------
  turnover: {
    label: 'Staff turnover',
    definition:
      'Exits divided by the average number in post over the window. People who left on a contract extension or renewal code are not counted as exits, as in the Remco data pack, and are shown separately.',
    caveats: ['Runs from 1 January to the date shown and is recalculated each day. It does not follow the period picker.'],
  },
  turnover_rate: {
    label: 'Turnover rate',
    definition: 'True exits over the window as a percentage of average headcount in that window.',
    caveats: ['Not annualised — it is the rate for the window shown on the panel.'],
  },
  leavers: {
    label: 'True exits',
    definition:
      'People who left during the window. People who left on a contract extension or renewal code are shown separately, not here.',
  },
  avg_headcount: {
    label: 'Average headcount',
    definition: 'Mean headcount across the window, the denominator of the turnover rate.',
  },
  conversions: {
    label: 'Leavers set apart: contract extended or renewed',
    definition:
      'People who left during the window whose last contract was closed with the Dynamics F&O code for a contract extension (CE) or renewal (CR). They hold no position at the date shown. As in the Remco data pack, they are not counted as exits.',
    caveats: [
      'These are people who left, not movement inside the workforce: a contract that was extended or renewed and carried on is not counted here.',
      'People who left on the other contract-extension codes (a date or rate change) are counted as exits, under End of contract.',
    ],
  },
  exit_reasons: {
    label: 'Exit reasons',
    definition:
      'Why people left during the window: the reason in Dynamics F&O, or where F&O holds none, the reason held by Sage payroll.',
    caveats: [
      "'Reason not recorded' means neither Dynamics F&O nor Sage payroll holds a reason for that exit.",
      "When payroll's record could not be checked for the date shown, the reasons are not shown.",
    ],
  },
  mobility: {
    label: 'Internal mobility',
    definition:
      'Starts in a different position over the twelve months to the date shown, counted as the Remco data pack counts them.',
    caveats: [
      'People returning after a year or more with no position, and people starting again a position they held in the twelve months before, are inside the totals but are not movement inside the workforce. Both are shown separately.',
    ],
  },
  // ---- span ---------------------------------------------------------------
  span_of_control: {
    label: 'Span of control',
    definition:
      'Direct reports per manager on the reporting lines as they stand at the panel date. Responds to the basis toggle.',
  },
  span_managers: {
    label: 'Managers',
    definition: 'People with at least one direct report on the selected basis.',
  },
  avg_span: { label: 'Average span', definition: 'Mean number of direct reports per manager.' },
  median_span: {
    label: 'Median span',
    definition: 'The middle manager’s number of direct reports — less distorted by very wide spans.',
  },
  max_span: { label: 'Widest span', definition: 'The largest number of direct reports held by one manager.' },
  span_distribution: {
    label: 'Span distribution',
    definition: 'Managers grouped by how many direct reports they carry.',
  },
  span_reports: {
    label: 'Reports',
    definition: 'Filled positions reporting to a filled manager position on the selected basis.',
  },
  span_range: {
    label: 'Range',
    definition: 'The smallest and the widest number of direct reports held by one manager.',
  },
  span_by_band: {
    label: 'Span by grade band',
    definition:
      'Managers and their average span in each grade band, as the Role Master grades their positions today.',
    caveats: [
      'A re-grade moves a manager between bands with no change in the structure. The data pack of September 2026 read the grades of 31 July.',
    ],
  },
  span_by_manager_role: {
    label: 'Largest manager roles',
    definition: 'The ten roles held by the most managers, with the average and range of their spans.',
  },
  managers_of_managers: {
    label: 'Managers of managers',
    definition:
      'A manager manages managers when at least one person reporting to them manages employees of their own.',
  },
  span_coverage: {
    label: 'Who is outside the figures',
    definition:
      'People reporting to a manager position that is vacant, or with no reporting line, are not in any span. They are counted here so nobody drops out unseen, with the same figures counting their stand-in leave approvers as managers.',
  },
  // ---- pay ----------------------------------------------------------------
  paid_headcount_fte: {
    label: 'Paid headcount and FTE',
    definition:
      'Everyone Finance paid in each payroll month and the full-time equivalents they represent — salaried staff count as one, an independent contractor as the share their month’s cost represents, capped at one.',
    source: 'Finance payroll workbook',
    caveats: [
      'Dated by payroll month; it moves when Finance closes a month, not with the establishment.',
      'Every payroll type is counted, interns and learners included.',
    ],
  },
  paid_headcount: { label: 'People paid', definition: 'Distinct people paid in that month.' },
  paid_fte: {
    label: 'FTE (capped)',
    definition: 'Full-time-equivalent conversion of that month’s paid population, capped at 1.0 per person.',
  },
  // ---- ratio --------------------------------------------------------------
  students_per_lecturer_fte: {
    label: 'Students per lecturer FTE',
    definition:
      'Invoiced students divided by the full-time equivalents of lecturers paid in the month, per campus.',
    source: 'Finance student file and payroll workbook',
    caveats: ['Dated by payroll month — it does not follow the period picker.'],
  },
  students: { label: 'Invoiced students', definition: 'Students invoiced at that campus for the period.' },
  lecturer_fte: {
    label: 'Lecturer FTE',
    definition:
      'Full-time equivalents of everyone paid in the month who holds a lecturer position, at the campus they are paid at.',
  },
  ratio: {
    label: 'Students per FTE',
    definition: 'Invoiced students divided by lecturer FTE. Higher means a thinner teaching resource.',
  },
  campus: { label: 'Campus', definition: 'Campus code as carried in F&O.' },
};

function DefinitionBody({ m }: { m: MetricDefinition }) {
  return (
    <div className="space-y-1.5">
      {m.label ? <p className="font-semibold text-white">{m.label}</p> : null}
      <p>{m.definition}</p>
      {m.source ? <p className="text-[11px] text-gray-400">Source: {m.source}</p> : null}
      {m.basis ? <p className="text-[11px] text-gray-400">Basis: {m.basis}</p> : null}
      {m.caveats?.length ? (
        <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
          {m.caveats.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** key -> tooltip body, or undefined when nothing is defined for that key. */
export type DefLookup = (key: string) => ReactNode | undefined;

export function buildDefLookup(metrics: MetricDefinition[] | null): DefLookup {
  const byKey = new Map<string, MetricDefinition>();
  (metrics ?? []).forEach((m) => {
    if (m?.key) byKey.set(m.key.toLowerCase(), m);
  });
  const localByKey = new Map<string, MetricDefinition>();
  Object.entries(LOCAL_DEFINITIONS).forEach(([k, v]) => localByKey.set(k.toLowerCase(), { key: k, ...v }));

  return (key: string) => {
    if (!key) return undefined;
    const k = key.toLowerCase();
    const m = byKey.get(k) ?? localByKey.get(k);
    return m ? <DefinitionBody m={m} /> : undefined;
  };
}

/**
 * The tooltip affordance. Renders nothing when there is no definition for the
 * key, so a missing entry degrades to a plain label rather than an empty bubble.
 *
 * Uses InfoTooltip (portal-based, provider-free) rather than the Radix Tooltip —
 * TooltipProvider is not mounted at app root and a Radix tooltip outside one
 * silently no-ops with no console warning.
 */
export const Def = ({
  def,
  k,
  text,
  className,
}: {
  def: DefLookup;
  k: string;
  text?: ReactNode;
  className?: string;
}) => {
  const body = text ?? def(k);
  if (!body) return null;
  return <InfoTooltip text={body} className={className} />;
};
